import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App, Button, Card, Form, Input, Select, Space, Typography } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '@/auth/useAuth';
import { friendlyAuthError } from '@/lib/errors';
import type { EnterpriseOption, LoginRequest } from '@/types/auth';
import './LoginPage.css';

const { Title, Text } = Typography;

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [enterprises, setEnterprises] = useState<EnterpriseOption[] | null>(null);
  const [credentials, setCredentials] = useState<LoginRequest | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [enterpriseForm] = Form.useForm();

  const from = (location.state as LocationState)?.from?.pathname ?? '/';

  async function onLogin(values: LoginRequest) {
    setLoading(true);
    try {
      const result = await login(values);

      if (result.requiresSelection && result.availableEnterprises) {
        // Multi-enterprise: mostrar selector
        setEnterprises(result.availableEnterprises);
        setCredentials(values);
        message.info('Selecciona la empresa con la que queres trabajar.');
      } else {
        message.success('Bienvenido');
        navigate(from, { replace: true });
      }
    } catch (err) {
      message.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function onSelectEnterprise(values: { enterpriseId: number }) {
    if (!credentials) return;
    setLoading(true);
    try {
      // Re-loginear con la enterprise especifica
      await login({ ...credentials, enterpriseId: values.enterpriseId });
      message.success('Bienvenido');
      navigate(from, { replace: true });
    } catch (err) {
      message.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  // ===== Render: selector de Enterprise =====
  if (enterprises && enterprises.length > 0) {
    return (
      <Card className="login-card">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="login-card__header">
            <Title level={3} style={{ marginBottom: 4 }}>
              Selecciona empresa
            </Title>
            <Text type="secondary">Tenes acceso a varias empresas. Elegi una para continuar.</Text>
          </div>
          <Form form={enterpriseForm} layout="vertical" onFinish={onSelectEnterprise}>
            <Form.Item
              name="enterpriseId"
              label="Empresa"
              rules={[{ required: true, message: 'Selecciona una empresa' }]}
            >
              <Select
                size="large"
                placeholder="Selecciona empresa"
                options={enterprises.map((e) => ({
                  value: e.enterpriseId,
                  label: `${e.displayNameEs || e.displayName} (${e.roleName})`,
                }))}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" size="large" htmlType="submit" loading={loading} block>
                Continuar
              </Button>
            </Form.Item>
          </Form>
          <Button
            type="link"
            block
            onClick={() => {
              setEnterprises(null);
              setCredentials(null);
              form.resetFields();
            }}
          >
            Volver al login
          </Button>
        </Space>
      </Card>
    );
  }

  // ===== Render: login normal =====
  return (
    <Card className="login-card">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="login-card__header">
          <Title level={2} style={{ marginBottom: 4 }}>
            MDM Platform
          </Title>
          <Text type="secondary">Inicia sesion para continuar</Text>
        </div>

        <Form form={form} layout="vertical" onFinish={onLogin} requiredMark={false}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingresa tu email' },
              { type: 'email', message: 'Email invalido' },
            ]}
          >
            <Input
              size="large"
              prefix={<MailOutlined />}
              placeholder="tu@email.com"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Ingresa tu password' }]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" size="large" htmlType="submit" loading={loading} block>
              Ingresar
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </Card>
  );
}
